// Genera un archivo .xlsx real (varias hojas, estilos, celdas combinadas y barras de datos)
// sin librerías externas. Un .xlsx es un ZIP con varios XML; aquí se arma sin compresión.

const TABLA_CRC = (() => {
  const tabla = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    tabla[n] = c >>> 0;
  }
  return tabla;
})();

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) crc = TABLA_CRC[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

function crearZip(archivos) {
  const codificador = new TextEncoder();
  const partes = [];
  const central = [];
  let desplazamiento = 0;

  archivos.forEach(({ nombre, contenido }) => {
    const nombreBytes = codificador.encode(nombre);
    const datos = codificador.encode(contenido);
    const crc = crc32(datos);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(6, 0x0800, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, datos.length, true);
    local.setUint32(22, datos.length, true);
    local.setUint16(26, nombreBytes.length, true);
    partes.push(local, nombreBytes, datos);

    const entrada = new DataView(new ArrayBuffer(46));
    entrada.setUint32(0, 0x02014b50, true);
    entrada.setUint16(4, 20, true);
    entrada.setUint16(6, 20, true);
    entrada.setUint16(8, 0x0800, true);
    entrada.setUint32(16, crc, true);
    entrada.setUint32(20, datos.length, true);
    entrada.setUint32(24, datos.length, true);
    entrada.setUint16(28, nombreBytes.length, true);
    entrada.setUint32(42, desplazamiento, true);
    central.push(entrada, nombreBytes);

    desplazamiento += 30 + nombreBytes.length + datos.length;
  });

  const tamanoCentral = central.reduce((suma, p) => suma + p.byteLength, 0);
  const fin = new DataView(new ArrayBuffer(22));
  fin.setUint32(0, 0x06054b50, true);
  fin.setUint16(8, archivos.length, true);
  fin.setUint16(10, archivos.length, true);
  fin.setUint32(12, tamanoCentral, true);
  fin.setUint32(16, desplazamiento, true);

  return new Blob([...partes, ...central, fin], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

const escaparXml = (valor) =>
  String(valor ?? "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export const letraColumna = (indice) => String.fromCharCode(65 + indice);

// Excel cuenta los días desde el 30/12/1899.
export const fechaExcel = (fecha) => {
  const f = new Date(fecha);
  return (Date.UTC(f.getFullYear(), f.getMonth(), f.getDate()) - Date.UTC(1899, 11, 30)) / 86400000;
};

// Paleta institucional (la misma del correo): azul #0b3c60 y dorado #c9a227.
const AZUL = "FF0B3C60";
const DORADO = "FFC9A227";
const GRIS_TEXTO = "FF5B6472";
const LINEA = "FFD3D9DF";

const FORMATOS = [
  { id: 164, codigo: '"C$" #,##0.00' },
  { id: 165, codigo: "dd/mm/yyyy" },
  { id: 166, codigo: "0.0%" },
];

const FUENTES = [
  "<sz val=\"11\"/><name val=\"Calibri\"/>",
  `<b/><sz val="11"/><color rgb="${AZUL}"/><name val="Calibri"/>`,
  `<b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/>`,
  `<b/><sz val="18"/><color rgb="FFFFFFFF"/><name val="Calibri"/>`,
  `<i/><sz val="10"/><color rgb="${GRIS_TEXTO}"/><name val="Calibri"/>`,
  `<b/><sz val="9"/><color rgb="${GRIS_TEXTO}"/><name val="Calibri"/>`,
  `<b/><sz val="18"/><color rgb="${AZUL}"/><name val="Calibri"/>`,
  `<b/><sz val="12"/><color rgb="${AZUL}"/><name val="Calibri"/>`,
  `<sz val="10"/><color rgb="${GRIS_TEXTO}"/><name val="Calibri"/>`,
];

const RELLENOS = [
  "<patternFill patternType=\"none\"/>",
  "<patternFill patternType=\"gray125\"/>",
  `<patternFill patternType="solid"><fgColor rgb="${AZUL}"/><bgColor indexed="64"/></patternFill>`,
  "<patternFill patternType=\"solid\"><fgColor rgb=\"FFEEF3FA\"/><bgColor indexed=\"64\"/></patternFill>",
  "<patternFill patternType=\"solid\"><fgColor rgb=\"FFF7F9FC\"/><bgColor indexed=\"64\"/></patternFill>",
];

const lado = (nombre, estilo, color) =>
  estilo ? `<${nombre} style="${estilo}"><color rgb="${color}"/></${nombre}>` : `<${nombre}/>`;
const borde = ({ izq, der, arriba, abajo } = {}) =>
  `<border>${lado("left", ...(izq || []))}${lado("right", ...(der || []))}${lado("top", ...(arriba || []))}${lado(
    "bottom",
    ...(abajo || [])
  )}<diagonal/></border>`;
const fino = ["thin", LINEA];

const BORDES = [
  borde(),
  borde({ izq: fino, der: fino, arriba: fino, abajo: fino }),
  borde({ abajo: ["medium", DORADO] }),
  borde({ izq: fino, der: fino, arriba: ["medium", AZUL], abajo: fino }),
  borde({ abajo: ["thick", DORADO] }),
  borde({ izq: ["thick", DORADO], der: fino, arriba: fino, abajo: fino }),
];

// Estilos con nombre: { fuente, relleno, borde, formato, alineacion }.
const ESTILOS = {
  normal: {},
  banda: { fuente: 3, relleno: 2, borde: 4, alineacion: { vertical: "center", indent: 1 } },
  bandaVacia: { relleno: 2, borde: 4 },
  subtitulo: { fuente: 4 },
  nota: { fuente: 8 },
  kpiEtiqueta: { fuente: 5, relleno: 3, borde: 5, alineacion: { vertical: "bottom", indent: 1 } },
  kpiValor: { fuente: 6, relleno: 3, borde: 5, formato: 164, alineacion: { horizontal: "left", vertical: "top", indent: 1 } },
  kpiNumero: { fuente: 6, relleno: 3, borde: 5, alineacion: { horizontal: "left", vertical: "top", indent: 1 } },
  seccion: { fuente: 7, borde: 2, alineacion: { vertical: "bottom" } },
  encabezado: { fuente: 2, relleno: 2, borde: 1, alineacion: { vertical: "center", indent: 1 } },
  encabezadoDerecha: { fuente: 2, relleno: 2, borde: 1, alineacion: { horizontal: "right", vertical: "center" } },
  texto: { borde: 1, alineacion: { indent: 1 } },
  textoCebra: { relleno: 4, borde: 1, alineacion: { indent: 1 } },
  monto: { borde: 1, formato: 164 },
  montoCebra: { relleno: 4, borde: 1, formato: 164 },
  porcentaje: { borde: 1, formato: 166 },
  porcentajeCebra: { relleno: 4, borde: 1, formato: 166 },
  fecha: { borde: 1, formato: 165, alineacion: { horizontal: "center" } },
  fechaCebra: { relleno: 4, borde: 1, formato: 165, alineacion: { horizontal: "center" } },
  centro: { borde: 1, alineacion: { horizontal: "center" } },
  centroCebra: { relleno: 4, borde: 1, alineacion: { horizontal: "center" } },
  totalTexto: { fuente: 1, relleno: 3, borde: 3, alineacion: { indent: 1 } },
  totalMonto: { fuente: 1, relleno: 3, borde: 3, formato: 164 },
  totalPorcentaje: { fuente: 1, relleno: 3, borde: 3, formato: 166 },
};
const NOMBRES_ESTILO = Object.keys(ESTILOS);

function xmlEstilos() {
  const xf = ({ fuente = 0, relleno = 0, borde: b = 0, formato = 0, alineacion }) => {
    const atributos = Object.entries(alineacion || {})
      .map(([k, v]) => `${k}="${v}"`)
      .join(" ");
    return `<xf numFmtId="${formato}" fontId="${fuente}" fillId="${relleno}" borderId="${b}" xfId="0"${
      formato ? ' applyNumberFormat="1"' : ""
    } applyFont="1" applyFill="1" applyBorder="1"${alineacion ? ` applyAlignment="1"><alignment ${atributos}/></xf>` : "/>"}`;
  };
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="${FORMATOS.length}">${FORMATOS.map((f) => `<numFmt numFmtId="${f.id}" formatCode="${escaparXml(f.codigo)}"/>`).join("")}</numFmts>
<fonts count="${FUENTES.length}">${FUENTES.map((f) => `<font>${f}</font>`).join("")}</fonts>
<fills count="${RELLENOS.length}">${RELLENOS.map((f) => `<fill>${f}</fill>`).join("")}</fills>
<borders count="${BORDES.length}">${BORDES.join("")}</borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="${NOMBRES_ESTILO.length}">${NOMBRES_ESTILO.map((n) => xf(ESTILOS[n])).join("")}</cellXfs>
</styleSheet>`;
}

/**
 * Una celda es { valor, estilo, formula } o null para dejarla vacía.
 * Los números se guardan como números; el texto, como texto.
 */
function xmlCelda(ref, celda) {
  if (!celda) return "";
  const s = Math.max(0, NOMBRES_ESTILO.indexOf(celda.estilo || "normal"));
  if (celda.formula) {
    return `<c r="${ref}" s="${s}"><f>${escaparXml(celda.formula)}</f><v>${Number(celda.valor || 0)}</v></c>`;
  }
  if (celda.valor === undefined || celda.valor === null || celda.valor === "") return `<c r="${ref}" s="${s}"/>`;
  if (typeof celda.valor === "number") return `<c r="${ref}" s="${s}"><v>${celda.valor}</v></c>`;
  return `<c r="${ref}" t="inlineStr" s="${s}"><is><t xml:space="preserve">${escaparXml(celda.valor)}</t></is></c>`;
}

/**
 * hoja: {
 *   nombre, anchos: [números], filas: [{ alto, celdas: [celda] }],
 *   combinar: ["A1:H1"], barras: ["C5:C9"], congelar: filas fijas arriba,
 *   filtro: "A4:H9", sinCuadricula: true
 * }
 */
function xmlHoja(hoja) {
  const filas = hoja.filas
    .map((fila, i) => {
      if (!fila) return "";
      const r = i + 1;
      const alto = fila.alto ? ` ht="${fila.alto}" customHeight="1"` : "";
      const celdas = (fila.celdas || []).map((c, j) => xmlCelda(`${letraColumna(j)}${r}`, c)).join("");
      return `<row r="${r}"${alto}>${celdas}</row>`;
    })
    .join("");

  const vista = `<sheetView workbookViewId="0"${hoja.sinCuadricula ? ' showGridLines="0"' : ""}>${
    hoja.congelar
      ? `<pane ySplit="${hoja.congelar}" topLeftCell="A${hoja.congelar + 1}" activePane="bottomLeft" state="frozen"/>`
      : ""
  }</sheetView>`;

  const barras = (hoja.barras || [])
    .map(
      (rango, i) =>
        `<conditionalFormatting sqref="${rango}"><cfRule type="dataBar" priority="${i + 1}"><dataBar><cfvo type="num" val="0"/><cfvo type="max"/><color rgb="FF5B8DC9"/></dataBar></cfRule></conditionalFormatting>`
    )
    .join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>
<sheetViews>${vista}</sheetViews>
<sheetFormatPr defaultRowHeight="16"/>
<cols>${hoja.anchos.map((ancho, i) => `<col min="${i + 1}" max="${i + 1}" width="${ancho}" customWidth="1"/>`).join("")}</cols>
<sheetData>${filas}</sheetData>
${hoja.filtro ? `<autoFilter ref="${hoja.filtro}"/>` : ""}
${hoja.combinar?.length ? `<mergeCells count="${hoja.combinar.length}">${hoja.combinar.map((m) => `<mergeCell ref="${m}"/>`).join("")}</mergeCells>` : ""}
${barras}
<pageMargins left="0.5" right="0.5" top="0.6" bottom="0.6" header="0.3" footer="0.3"/>
<pageSetup orientation="landscape" fitToWidth="1" fitToHeight="0"/>
</worksheet>`;
}

export function descargarLibroExcel({ nombreArchivo, hojas }) {
  const nombreHoja = (h) => escaparXml(h.nombre).slice(0, 31);
  const filtros = hojas
    .map((h, i) =>
      h.filtro
        ? `<definedName name="_xlnm._FilterDatabase" localSheetId="${i}" hidden="1">'${nombreHoja(h)}'!${h.filtro
            .split(":")
            .map((ref) => ref.replace(/([A-Z]+)(\d+)/, "$$$1$$$2"))
            .join(":")}</definedName>`
        : ""
    )
    .join("");

  const archivo = crearZip([
    {
      nombre: "[Content_Types].xml",
      contenido: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
${hojas
  .map(
    (_, i) =>
      `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
  )
  .join("")}
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`,
    },
    {
      nombre: "_rels/.rels",
      contenido: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
    },
    {
      nombre: "xl/workbook.xml",
      contenido: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>${hojas.map((h, i) => `<sheet name="${nombreHoja(h)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join("")}</sheets>
${filtros ? `<definedNames>${filtros}</definedNames>` : ""}
</workbook>`,
    },
    {
      nombre: "xl/_rels/workbook.xml.rels",
      contenido: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${hojas
  .map(
    (_, i) =>
      `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`
  )
  .join("")}
<Relationship Id="rId${hojas.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
    },
    ...hojas.map((h, i) => ({ nombre: `xl/worksheets/sheet${i + 1}.xml`, contenido: xmlHoja(h) })),
    { nombre: "xl/styles.xml", contenido: xmlEstilos() },
  ]);

  const enlace = document.createElement("a");
  enlace.href = URL.createObjectURL(archivo);
  enlace.download = nombreArchivo;
  enlace.click();
  setTimeout(() => URL.revokeObjectURL(enlace.href), 1000);
}
